import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [method, setMethod] = useState("email"); // "email" | "questions"

    // Estado preguntas de seguridad
    const [form, setForm] = useState({ email: "", birthday_day: "", city: "", pet: "", new_password: "" });
    // Estado flujo por email
    const [emailForm, setEmailForm] = useState({ email: "" });

    const [error, setError] = useState("");
    const [ok, setOk] = useState("");
    const [loading, setLoading] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const setEmail = (e) => setEmailForm({ email: e.target.value });

    const clearMessages = () => { setError(""); setOk(""); };

    const submitQuestions = async (e) => {
        e.preventDefault();
        clearMessages(); setLoading(true);
        try {
            await api.post("/auth/forgot-password", form);
            setOk("Contraseña actualizada. Ya puedes iniciar sesión.");
            setTimeout(() => navigate("/login"), 1500);
        } catch (err) {
            setError(formatApiError(err, "No pudimos verificar tu identidad"));
        } finally { setLoading(false); }
    };

    const submitEmail = async (e) => {
        e.preventDefault();
        clearMessages(); setLoading(true);
        try {
            const res = await api.post("/auth/forgot-password/email", emailForm);
            setOk(res.data?.message || "Si el correo está registrado, recibirás una contraseña temporal en tu bandeja.");
        } catch (err) {
            setError(formatApiError(err, "No pudimos procesar la solicitud"));
        } finally { setLoading(false); }
    };

    return (
        <div className="auth-shell" data-testid="forgot-page">
            <section className="auth-hero">
                <div className="brand">itotal<span className="accent">4u</span></div>
                <div>
                    <h2>Recupera tu <em>acceso</em>.</h2>
                    <p className="tag">
                        {method === "email"
                            ? "Te enviaremos una contraseña temporal a tu correo. Al iniciar sesión con ella el sistema te pedirá definir una nueva."
                            : "Responde las 3 preguntas que configuraste al crear tu cuenta. Si eres superadmin, contacta al administrador con la master key."}
                    </p>
                </div>
                <div className="meta" data-testid="forgot-method-indicator">
                    método: {method === "email" ? "correo electrónico" : "preguntas de seguridad"}
                </div>
            </section>
            <section className="auth-pane">
                <div className="auth-card">
                    <h3>Recuperar contraseña</h3>
                    <p className="sub">Elige cómo quieres recuperar tu acceso.</p>

                    {/* Selector de método */}
                    <div className="segmented" style={{ marginBottom: 16 }} role="tablist" aria-label="Método de recuperación" data-testid="forgot-method-tabs">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={method === "email"}
                            className={`segmented-btn ${method === "email" ? "is-active" : ""}`}
                            onClick={() => { setMethod("email"); clearMessages(); }}
                            data-testid="forgot-method-email"
                            style={{
                                color: method === "email" ? "#fff" : "var(--text)",
                                background: method === "email" ? "var(--accent)" : "transparent",
                                borderColor: method === "email" ? "var(--accent)" : "var(--border)",
                            }}
                        >
                            Por correo
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={method === "questions"}
                            className={`segmented-btn ${method === "questions" ? "is-active" : ""}`}
                            onClick={() => { setMethod("questions"); clearMessages(); }}
                            data-testid="forgot-method-questions"
                            style={{
                                color: method === "questions" ? "#fff" : "var(--text)",
                                background: method === "questions" ? "var(--accent)" : "transparent",
                                borderColor: method === "questions" ? "var(--accent)" : "var(--border)",
                            }}
                        >
                            Por preguntas de seguridad
                        </button>
                    </div>

                    {error && <div className="alert alert-error" data-testid="forgot-error">{error}</div>}
                    {ok && <div className="alert alert-ok" data-testid="forgot-ok">{ok}</div>}

                    {method === "email" ? (
                        <form onSubmit={submitEmail} className="row" style={{ gap: 14 }} data-testid="forgot-form-email">
                            <div className="field">
                                <label>Correo electrónico</label>
                                <input type="email" className="input" value={emailForm.email} onChange={setEmail} required data-testid="forgot-email-input" />
                            </div>
                            <button className="btn btn-primary btn-block" type="submit" disabled={loading} data-testid="forgot-email-submit">
                                {loading ? <span className="loader" /> : "Enviarme contraseña temporal"}
                            </button>
                            <p className="text-muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
                                La contraseña temporal expira automáticamente cuando definas una nueva en tu siguiente login.
                            </p>
                        </form>
                    ) : (
                        <form onSubmit={submitQuestions} className="row" style={{ gap: 14 }} data-testid="forgot-form-questions">
                            <div className="field">
                                <label>Correo electrónico</label>
                                <input type="email" className="input" value={form.email} onChange={set("email")} required data-testid="forgot-email" />
                            </div>
                            <div className="row row-3">
                                <div className="field">
                                    <label>Día de cumpleaños</label>
                                    <input className="input" value={form.birthday_day} onChange={set("birthday_day")} required data-testid="forgot-birthday" />
                                </div>
                                <div className="field">
                                    <label>Ciudad</label>
                                    <input className="input" value={form.city} onChange={set("city")} required data-testid="forgot-city" />
                                </div>
                                <div className="field">
                                    <label>Mascota</label>
                                    <input className="input" value={form.pet} onChange={set("pet")} required data-testid="forgot-pet" />
                                </div>
                            </div>
                            <div className="field">
                                <label>Nueva contraseña</label>
                                <input type="password" className="input" value={form.new_password} onChange={set("new_password")} required minLength={8} data-testid="forgot-newpw" />
                            </div>
                            <button className="btn btn-primary btn-block" type="submit" disabled={loading} data-testid="forgot-submit">
                                {loading ? <span className="loader" /> : "Actualizar contraseña"}
                            </button>
                        </form>
                    )}
                    <div className="switch"><Link to="/login" data-testid="forgot-to-login">← Volver al login</Link></div>
                </div>
            </section>
        </div>
    );
}
